
import React from "react";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { ExternalLink, ArrowRight, Camera, Shield, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const Resources = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const isNativeApp = Capacitor.isNativePlatform();

  const handleExternalLink = async (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    if (isNativeApp) {
      e.preventDefault();
      await Browser.open({ url });
    }
  };

  const resources = [
    {
      title: "TSA Film Hand Check Guidelines",
      description: "Official TSA guidelines for requesting hand checks for your film at airport security",
      link: "https://www.tsa.gov/travel/security-screening/whatcanibring/items/film",
      isExternal: true,
      icon: <Shield className="h-5 w-5 mr-2" />
    },
    {
      title: "Ilford Film Hand Check FAQ",
      description: "Ilford's recommendations for getting film hand-checked at security checkpoints",
      link: "https://www.ilfordphoto.com/faqs/#:~:text=If%20you%20need%20to%20travel,3200%2C%20this%20is%20our%20recommendation.",
      isExternal: true,
      icon: <Camera className="h-5 w-5 mr-2" />
    },
    {
      title: "Kodak Film Transport & Hand Check Guide",
      description: "Guidelines for safely transporting film and requesting hand checks, includes free printables",
      link: "https://www.kodak.com/en/motion/page/transporting-storing-film/",
      isExternal: true,
      icon: <Camera className="h-5 w-5 mr-2" />
    },
    {
      title: "X-Ray Effects on Film By Lina Bessonova",
      description: "Research on how X-ray scanners affect film and why hand checks are important",
      link: "https://www.handcheckfilm.com/scanner-damage",
      isExternal: true,
      icon: <Shield className="h-5 w-5 mr-2" />
    },
    {
      title: "Film Photographers Hand Check Community",
      description: "Online communities for film photographers discussing hand check experiences at airports",
      link: "https://www.reddit.com/r/AnalogCommunity/",
      isExternal: true,
      icon: <Camera className="h-5 w-5 mr-2" />
    },
    {
      title: "Share Your Hand Check Experience",
      description: "Contribute your airport film hand check experiences to help other photographers",
      link: "/new-review",
      isExternal: false,
      icon: <ArrowRight className="h-5 w-5 mr-2" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-background"
    >
      <Helmet>
        <title>Film Hand Check Guide | Airport Security Resources for Photographers</title>
        <meta name="description" content="Essential film hand check resources and guides for photographers. Learn how to protect your film from X-ray damage with our comprehensive hand check guides, TSA guidelines, and photographer experiences." />
        <meta name="keywords" content="film hand check, airport film security, TSA film guidelines, x-ray film damage, hand check resources, how to hand check film, film scanner damage" />
        <link rel="canonical" href="https://safelightproject.com/resources" />
      </Helmet>

      <main className={`container mx-auto ${isMobile ? 'px-3 py-3' : 'px-4 py-6'} max-w-4xl`}>
        <div className={`${isMobile ? 'mt-2' : 'my-6'}`}>
          <h1 className={`text-${isMobile ? '2xl' : '3xl'} font-bold mb-2`}>Film Hand Check Resources & Guides</h1>
          <p className={`text-${isMobile ? 'base' : 'lg'} text-muted-foreground mb-6`}>
            Essential guides and information for successfully getting film hand-checked at airport security worldwide. Protect your film from X-ray damage.
          </p>
          
          <ul className="space-y-4">
            {resources.map((resource, resourceIndex) => (
              <li key={resourceIndex} className="bg-card hover:bg-accent/50 rounded-md p-4 transition-colors">
                {resource.isExternal ? (
                  <a 
                    href={resource.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-start justify-between group"
                    onClick={(e) => handleExternalLink(e, resource.link)}
                  >
                    <div className="flex items-start">
                      {resource.icon}
                      <div>
                        <p className="font-medium group-hover:text-primary transition-colors">{resource.title}</p>
                        <p className="text-sm text-muted-foreground">{resource.description}</p>
                      </div>
                    </div>
                    <ExternalLink className="h-5 w-5 flex-shrink-0 mt-1 group-hover:text-primary transition-colors" />
                  </a>
                ) : (
                  <Link 
                    to={resource.link} 
                    className="flex items-start justify-between group"
                  >
                    <div className="flex items-start">
                      {resource.icon}
                      <div>
                        <p className="font-medium group-hover:text-primary transition-colors">{resource.title}</p>
                        <p className="text-sm text-muted-foreground">{resource.description}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 flex-shrink-0 mt-1 group-hover:text-primary transition-colors" />
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-8 bg-accent/20 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4" id="why-film-hand-checks">Why Film Hand Checks Matter</h2>
            <p className="mb-4">Modern CT scanners and X-ray machines at airport security can damage your undeveloped film. This is especially true for:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>High ISO film (400 and above) - more sensitive to X-ray damage</li>
              <li>Slide film (E-6 process) - particularly susceptible to fogging from scanners</li>
              <li>Film that will be put through multiple X-ray machines during connecting flights</li>
              <li>Professional and specialty films requiring pristine image quality</li>
            </ul>
            <p className="mb-4">Always request a hand check for your undeveloped film to protect your precious images from X-ray scanner damage. Our airport database can help you find airports where successful hand checks are routinely provided.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">How to Request a Film Hand Check:</h3>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li>Keep your film in a clear plastic bag separate from other items</li>
              <li>Politely inform security officers you have photographic film that needs a hand check</li>
              <li>Explain that X-rays and CT scanners can damage undeveloped film</li>
              <li>Be prepared to show film boxes or canisters as proof</li>
              <li>Arrive early to allow extra time for this security process</li>
            </ol>
            
            <p>Our database of airport reviews helps you prepare for your travel by knowing which airports are film-friendly and which might require extra persistence for successful hand checks.</p>
          </div>
        </div>
      </main>
    </motion.div>
  );
};

export default Resources;
